import React, { useState, useRef, useEffect } from "react";
import { TextField, CircularProgress, InputAdornment } from "@mui/material";
import { validateEmailFormat } from "../../Utilidades/Schemas/EmailSchema";
import {
  validarEmailZerobounce,
  enviarCodigoVerificacion,
} from "../../Actions/Companias/CompaniaAction";
import { useGlobalState } from "../../Utilidades/useGlobalState";
import { MostrarMensaje } from "../../Utilidades/ManejoMensajes";
import DialogoVerificacionEmail from "./DialogoVerificacionEmail";

// Cache en memoria para emails validados
const emailValidationCache = new Map();

/**
 * Componente ValidEmailInput
 * Input de email con validaciones personalizadas:
 * 1. Validación de formato con Yup
 * 2. Validación con Zerobounce (opcional)
 * 3. Validación con código de verificación (pendiente implementación)
 *
 * @param {Object} props
 * @param {boolean} props.isEmailValid - Estado actual de validación del email
 * @param {Function} props.setIsValidEmail - Función para actualizar estado de validación
 * @param {string} props.email - Email actual
 * @param {Function} props.setEmail - Función para actualizar el email
 * @param {Function} props.setValidationStatus - Función para indicar si se está validando
 * @param {boolean} props.doSpecialValidation - Indica si debe hacer validaciones especiales
 * @param {boolean} props.zerobounce - Indica si debe validar con Zerobounce
 * @param {string} props.zerobounceUrl - URL de la API de Zerobounce
 * @param {string} props.zerobounceApiKey - API Key de Zerobounce
 * @param {boolean} props.disabled - Si el input está deshabilitado
 * @param {string} props.label - Etiqueta del campo
 * @param {Object} props.textFieldProps - Props adicionales para TextField
 */
const ValidEmailInput = ({
  isEmailValid = false,
  setIsValidEmail,
  email,
  setEmail,
  setValidationStatus,
  doSpecialValidation = false,
  zerobounce = false,
  zerobounceUrl = "",
  zerobounceApiKey = "",
  disabled = false,
  label = "",
  textFieldProps = {},
  companyId = null,
  customerId = null,
  languageCode = "ES",
}) => {
  const emailInputRef = useRef(null);
  const validationTimeoutRef = useRef(null);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasError, setHasError] = useState(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");

  const { etiquetas, dispatch } = useGlobalState();

  // Limpieza del timeout al desmontar
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Envía el código de verificación al email
   */
  const sendVerificationCode = async (inputEmail) => {
    try {
      const result = await enviarCodigoVerificacion(
        companyId,
        inputEmail,
        customerId,
        languageCode,
      );

      if (result.success) {
        // Guardar email a verificar y abrir dialog
        setEmailToVerify(inputEmail);
        setOpenVerificationDialog(true);
        MostrarMensaje(dispatch, "success", ["VerificationCodeSent"]);
      } else {
        // Error al enviar código
        setIsValidEmail(false);
        setHasError(true);
        MostrarMensaje(dispatch, "error", ["VerificationCodeError"]);
      }
    } catch (error) {
      console.error("Error sending verification code:", error);
      setIsValidEmail(false);
      setHasError(true);
      MostrarMensaje(dispatch, "error", ["VerificationCodeError"]);
    } finally {
      setIsValidating(false);
      setValidationStatus(false);
    }
  };

  /**
   * Maneja el éxito de la verificación del código
   */
  const handleVerificationSuccess = (verificationId) => {
    setIsValidEmail(true);
    setHasError(false);
    emailValidationCache.set(emailToVerify, true);
    setOpenVerificationDialog(false);
    setIsValidating(false);
    setValidationStatus(false);
  };

  /**
   * Maneja cancelacion de verificación del código
   */
  const handleVerificationCancel = (success) => {
    setIsValidEmail(false);
    setHasError(true);
    setOpenVerificationDialog(false);
    setIsValidating(false);
    setValidationStatus(false);
  };

  /**
   * Maneja errores en la verificación del código
   */
  const handleVerificationError = (errorMessage) => {
    setIsValidEmail(false);
    setHasError(true);
    // No cerrar el dialog, permitir reintentar
  };

  /**
   * Maneja el cierre del dialog (no se usa porque el dialog no puede cerrarse)
   */
  const handleCloseDialog = () => {};

  /**
   * Ejecuta todas las validaciones del email en cascada
   */
  const validateEmail = async (inputEmail) => {
    if (!inputEmail || inputEmail.trim() === "") {
      setIsValidEmail(false);
      setHasError(false);
      setErrorMessage("");
      return;
    }

    // Iniciar validación
    setIsValidating(true);
    setValidationStatus(true);
    setHasError(false);
    setErrorMessage("");

    try {
      // Paso 1: Validación de formato con Yup
      const isFormatValid = await validateEmailFormat(inputEmail);

      if (!isFormatValid) {
        setIsValidEmail(false);
        setHasError(true);
        // MostrarMensaje(dispatch, "error", ["InvalidEmailFormat"]);
        return;
      }

      // Si no se requieren validaciones especiales, el email es válido
      if (!doSpecialValidation) {
        setIsValidEmail(true);
        return;
      }

      // Verificar cache antes de validaciones especiales
      if (emailValidationCache.has(inputEmail)) {
        const cachedResult = emailValidationCache.get(inputEmail);
        setIsValidEmail(cachedResult);
        return;
      }

      // Paso 2: Validación con Zerobounce (si está habilitado)
      if (zerobounce && zerobounceUrl && zerobounceApiKey) {
        const zerobounceResult = await validarEmailZerobounce(
          inputEmail,
          zerobounceUrl,
          zerobounceApiKey,
        );

        if (zerobounceResult.success && zerobounceResult.status === "valid") {
          // Email válido según Zerobounce
          setIsValidEmail(true);
          emailValidationCache.set(inputEmail, true);
          MostrarMensaje(dispatch, "success", ["VerificationCodeSuccess"]);
          return;
        } else {
          // Email inválido según Zerobounce o falla zerobounce
          setIsValidEmail(false);
          setHasError(true);
        }
      }

      // Paso 3: Validación con código de verificación
      await sendVerificationCode(inputEmail);
    } catch (error) {
      console.error("Error validating email:", error);
      setIsValidEmail(false);
      setHasError(true);
      MostrarMensaje(dispatch, "error", ["ErrorValidatingEmail"]);
    } finally {
      setIsValidating(false);
      setValidationStatus(false);
    }
  };

  /**
   * Maneja cambios en el input
   */
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(e); // se envia el evento completo para compatibilidad
    setHasError(false);
    setErrorMessage("");

    // Limpiar timeout anterior si existe
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Solo validar formato básicamente mientras escribe
    if (newEmail && newEmail.trim() !== "") {
      validateEmailFormat(newEmail).then((isFormatValid) => {
        if (!isFormatValid) {
          setHasError(true);
          setIsValidEmail(false);
        } else {
          setHasError(false);
          // No marcar como válido aún si requiere validaciones especiales
          if (!doSpecialValidation) {
            setIsValidEmail(true);
          }
        }
      });
    } else {
      setIsValidEmail(false);
      setHasError(false);
    }
  };

  /**
   * Maneja cuando el input pierde el foco para validaciones especiales
   */
  const handleEmailBlur = (e) => {
    const currentEmail = e.target.value;

    // Solo hacer validaciones especiales si están habilitadas y el formato es válido
    if (doSpecialValidation && currentEmail && currentEmail.trim() !== "") {
      // Limpiar timeout anterior si existe
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      // Ejecutar validación especial inmediatamente al perder foco
      validateEmail(currentEmail);
    }
  };

  return (
    <>
      <TextField
        inputRef={emailInputRef}
        type="email"
        name="cusMail"
        label={label || etiquetas?.General?.Et_Email || "Email"}
        value={email}
        onChange={handleEmailChange}
        onBlur={handleEmailBlur}
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        fullWidth
        InputProps={{
          endAdornment: isValidating && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
        }}
        {...textFieldProps}
      />

      {/* Dialog de verificación de email */}
      <DialogoVerificacionEmail
        open={openVerificationDialog}
        onClose={handleCloseDialog}
        email={emailToVerify}
        companyId={companyId}
        customerId={customerId}
        languageCode={languageCode}
        onSuccess={handleVerificationSuccess}
        onCancel={handleVerificationCancel}
        onError={handleVerificationError}
      />
    </>
  );
};

export default ValidEmailInput;
